import { computeArrayDiffs } from '../services/diff';
import initializePaging from './paging';

const NOT_LOADED = 0;
const LOADING = 1;
const LOADED = 2;

const DEBUG = false;
const log = (...args) => {
  if (DEBUG) {
    console.log('Store: ', ...args);
  }
};

const logEOL = () => {
  if (DEBUG) {
    console.log('\n');
  }
};

const INDENTS = ['', '    '];
const indent = (i) => {
  if (i < INDENTS.length) return INDENTS[i];
  let b = INDENTS[INDENTS.length - 1];
  for (let j = INDENTS.length; j <= i; j += 1) {
    b = `${b}${INDENTS[1]}`;
  }
  return b;
};

const logState = (msg, store) => {
  if (DEBUG) {
    if (msg) {
      log(msg);
    }
    const nodeNames = (f) => {
      return Object.values(store.entityNodes).filter(f).map(n => n.name).join(',');
    };
    log(
      'State: [',
      nodeNames(n => (n.loadState === LOADED) && !n.pendingData),
      ' pending: ',
      nodeNames(n => n.pendingData),
      ']',
    );
  }
};

class Store {
  constructor({ schemaService, authService, onError }) {
    this.schemaService = schemaService;
    if (authService) {
      this.authService = authService;
      this.authSub = authService.subscribe(({ isAuth }) => {
        if (!isAuth) {
          this.userLoggedOut();
        } else {
          this.userLoggedIn();
        }
      });
    }
    this.authService = authService;
    this.onError = onError;
    this.contexts = {};
    this.loadStates = {};
    this.entityNodes = {};
    this.subscriptions = [];
    this.parentEntities = {};
  }

  addEntity(entityName, entity) {
    const paging = initializePaging(entity.paging);
    const needContextualParams = entity.needContextualParams
      ? entity.needContextualParams.bind(entity)
      : (paging && paging.needContextualParams.bind(paging) || (() => false));
    const entityNode = {
      name: entityName,
      loadState: NOT_LOADED,
      paging,
      needContextualParams,
      context: Object.assign({ etag: entity.etag ? '' : undefined }, entity.context),
      data: paging ? paging.defaultValue : entity.defaultValue,
      defaultValue: paging ? paging.defaultValue : entity.defaultValue,
      entity,
      requiredEntityNodes: [],
      areDependenciesLoaded: () => true,
      requiredBy: [],
    };
    this.entityNodes[entityName] = entityNode;
    const { type } = entity;
    const computedProperties = type && this.schemaService.getComputedProperties(type);
    if (computedProperties) {
      entityNode.computedProperties = computedProperties;
      entityNode.requires = this.schemaService.getRequiredEntities(type);
    }
    if (entity.derivedEntities) {
      entity.derivedEntities.entities.forEach((name) => {
        this.parentEntities[name] = entityName;
      });
    }
    // Process configuration for computing diffs between old and new data for this entity
    if (entity.computeDiffs) {
      entityNode.diffs = {
        objectsById: {},
        dataDiffs: null,
      };
      const diffOptions = {};
      if (typeof entity.computeDiffs === 'boolean') {
        diffOptions.getId = o => o.id;
      } else {
        Object.assign(diffOptions, entity.computeDiffs);
      }
      const { getId } = diffOptions;
      delete diffOptions.getId;
      entityNode.diffs.compute = (oldArray, newArray) => {
        if (!oldArray || !oldArray.length) {
          if (!newArray || !newArray.length) {
            entityNode.diffs.objectsById = {};
            entityNode.diffs.dataDiffs = null;
          } else {
            const byIds = {};
            newArray.forEach((o, i) => {
              byIds[getId(o, i)] = o;
            });
            entityNode.diffs.objectsById = byIds;
            entityNode.diffs.dataDiffs = { newObjects: newArray };
          }
        } else if (!newArray || !newArray.length) {
          entityNode.diffs.objectsById = {};
          entityNode.diffs.dataDiffs = { deletedObjects: oldArray };
        } else {
          entityNode.diffs.dataDiffs = computeArrayDiffs(oldArray, newArray, getId, diffOptions);
        }
        return entityNode.diffs.dataDiffs;
      };
    }
  }

  configurationInitialized() {
    Object.keys(this.entityNodes).map(k => this.entityNodes[k]).filter(n => n.requires).forEach((entityNode) => {
      // if (entityNode.processingDepedencies) {
      //   throw new Error(`Depedency loop: entity ${entityNode.name} has a depedency to an entity that depends on ${entityNode.name}`);
      // }
      // entityNode.processingDepedencies = true;
      entityNode.requires.forEach((r) => {
        const rootName = this.parentEntities[r] || r;
        const reqEntity = this.entityNodes[rootName];
        if (!reqEntity) {
          throw new Error(`Entity ${r} registered as required property for entity ${entityNode.name} is not registered`);
        } else {
          if (reqEntity.requiredBy.indexOf(entityNode) < 0) {
            reqEntity.requiredBy.push(entityNode);
          }
          if (entityNode.requiredEntityNodes.indexOf(reqEntity) < 0) {
            entityNode.requiredEntityNodes.push(reqEntity);
          }
        }
      });
      entityNode.areDependenciesLoaded = () => !entityNode.requiredEntityNodes.find(n => n.loadState !== LOADED);
      // delete entityNode.processingDepedencies;
    });
  }

  clearCache(entityName) {
    this.entityNodes[entityName].data = this.entityNodes[entityName].defaultValue;
  }

  getData(entityName) {
    return this.entityNodes[entityName].data;
  }

  getDataOrLoad(entities, onLoad, onError) {
    const entityNames = entities.map(e => (typeof e === 'string' ? e : e.name));
    const pms = [];
    const result = {};
    entityNames.forEach((name) => {
      const parent = this.parentEntities[name];
      const node = this.entityNodes[parent || name];
      if (node.loadState === NOT_LOADED) {
        result[name] = node.defaultValue;
         // Loading with custom context to:
         // - not use query associated with the node
         // - not trigger polling if not associated with polling
        pms.push(this.load(parent || name), {});
      } else {
        result[name] = parent ? node.data[name] : node.data;
      }
    });
    // All entities provided as the parameter have been loaded
    if (!pms.length) {
      return result;
    }

    if (onLoad || onError) {
      Promise.all(pms)
      .then(() => {
        const r = {};
        entityNames.forEach((e) => {
          const parent = this.parentEntities[e];
          r[e] = parent
            ? this.entityNodes[parent].data[e] : this.entityNodes[e].data;
        });
        if (onLoad) {
          onLoad(result);
        }
        return r;
      })
      .catch((e) => {
        if (onError) {
          onError(e);
        }
      });
    }
    return result;
  }

  load(entityName, customCtx) {
    const entityNode = this.entityNodes[entityName];
    const { context, entity, requiredEntityNodes } = entityNode;
    entityNode.loadState = LOADING;
    const depLoads = requiredEntityNodes.map((n) => {
      switch (n.loadState) {
        case LOADING: return n.pms;
        case LOADED: return null;
        default: return this.load(n.name);
      }
    }).filter(p => p);
    const pms = (entityNode.paging
      ? entity.request({
          ...(customCtx || context),
          request: entityNode.paging.requestParams((customCtx || context).page, customCtx || context),
        })
      : entity.request(customCtx || context))
    .then((res) => {
      if (entityNode.cancelledLoad === pms) {
        entityNode.cancelledLoad = null;
        return null;
      }
      let { data } = res;
      if (!data || typeof data === 'string') {
        data = entityNode.defaultValue;
      }
      if (!customCtx) {
        if (entity.etag) {
          context.etag = res.headers.etag;
        }
        entityNode.pms = null;
        entityNode.error = false;
        this.setData(entityName, data);

        if (entity.polling) {
          entityNode.pollingHandle = setTimeout(() => {
            this.load(entityName);
          }, entity.getPolling ? entity.getPolling(entityNode.data, entity.polling) : entity.polling);
        }
      } else {
        customCtx.pms = null;
        customCtx.error = null;
        if (entity.derivedEntities) {
          return entity.derivedEntities.compute(data);
        }
      }
      return data;
    })
    .catch((error) => {
      if (error.response && (error.response.status === 304)) {
        if (!customCtx) {
          entityNode.error = false;
        } else {
          customCtx.error = false;
          return entityNode.defaultValue;
        }
      }

      if (customCtx) {
        customCtx.error = error;
        throw error;
      }

      if (!entityNode.error) {
        entityNode.error = true;
        const polling = entityNode.entity.pollingOnError || entityNode.entity.polling;
        this.onError(error);
        if (polling) {
          setTimeout(() => this.load(entityName), polling);
        }
      }
      try {
        this.setData(entityName, entityNode.defaultValue);
      } catch (e) {
        console.log(e);
      }
      return entityNode.defaultValue;
    });
    if (!customCtx) {
      entityNode.pms = pms;
    } else {
      customCtx.pms = pms;
    }
    if (!depLoads.length) {
      return pms;
    }
    return Promise.all([pms].concat(depLoads)).then((all) => {
      return all[0];
    });
  }

  fetch(entities, forceLoad) {
    if (!Array.isArray(entities)) {
      const parent = this.parentEntities[entities];
      const node = this.entityNodes[parent || entities];
      if ((node.loadState === NOT_LOADED) || forceLoad) {
        return this.load(parent || entities).then((data) => {
          return parent ? data[entities] : data;
        });
      }
      return Promise.resolve(parent ? node.data[entities] : node.data);
    }
    throw new Error('Not implemented!');
  }

  update(entityName) {
    const node = this.entityNodes[this.parentEntities[entityName] || entityName];
    if (node.paging) {
      this.setData(node.name, node.paging.clear());
      return Promise.resolve(null);
    }
    this.stopPolling(entityName);
    return this.load(entityName);
  }

  loadPage(page, entityName) {
    const node = this.entityNodes[this.parentEntities[entityName] || entityName];
    if (!node) {
      return Promise.reject(new Error(`Unkown entity ${entityName}`));
    }
    if (!node.paging) {
      return Promise.reject(new Error(`No paging configured for entity ${entityName}`));
    }
    node.context.page = page;
    return this.load(entityName).then(data => node.paging.toTable(data, page));
  }

  stopPolling(entityName) {
    const entityNode = this.entityNodes[entityName];
    let stopped = false;
    if (entityNode.pollingHandle) {
      clearTimeout(entityNode.pollingHandle);
      entityNode.pollingHandle = null;
      stopped = true;
    }
    if (entityNode.pms) {
      entityNode.cancelledLoad = entityNode.pms;
      entityNode.pms = null;
      stopped = true;
    }
    return stopped;
  }

  startPolling(entityName) {
    return this.update(entityName);
  }

  stopAllPollings() {
    return Object.keys(this.entityNodes).filter(name => this.stopPolling(name));
  }

  restartPollings(entityNames) {
    return Promise.all(entityNames.map(name => this.load(name)));
  }

  setData(entityName, data, ctx = { changes: {}, notify: true, level: 0 }) {
    const node = this.entityNodes[entityName];
    const { changes } = ctx;

    // Process computed properties
    const { computedProperties, requires } = node;
    if (computedProperties) {
      if (!ctx.passDependencyCheck && !node.areDependenciesLoaded()) {
        // New data not ready to be set as some entities used for
        // computing properties are not loaded yet
        node.pendingData = data;
        logState(`${indent(ctx.level)}Entity ${entityName} loaded but not computed`, this);
        return false;
      }
      // Dependent data is ready, prepare context for processing computed properties
      const compCtx = {};
      requires.forEach((name) => {
        const parent = this.parentEntities[name];
        compCtx[name] = parent ? this.entityNodes[parent].data[name] : this.entityNodes[name].data;
      });
      // Computing context is ready, compute properties
      const rows = node.paging ? node.paging.getDataRows(data, node.context.page) : (Array.isArray(data) && data || [data]);
      rows.forEach((o) => {
        computedProperties.forEach((prop) => { o[prop.name] = prop.compute(o, compCtx); });
      });
      node.pendingData = undefined;
    }

    // If no diffs between current data and this new loaded data, exit
    const { derivedEntities } = node.entity;
    // If derived entities, node.data contains derived entities data and not last raw data array with computed properties
    const prevData = derivedEntities ? node.underivedData : node.data;
    if (!node.paging && node.diffs && prevData && !node.diffs.compute(prevData, data)) {
      // If at least one subscription is found that depends on this entity and explicitely
      // requires an update (new subscriber), do not exit here
      if (!this.subscriptions.filter(sub => sub.needUpdate).find((sub) => {
        const { entitySubs } = sub;
        return entitySubs.find(([n]) => n.name === entityName);
      })) {
        log(`${indent(ctx.level)}  => setData on `, entityName, ' is inactive: computed diffs are null');
        return false;
      }
    }

    // Update entity data with provided data. Update derived data if any
    if (derivedEntities) {
      const udata = node.paging ? node.paging.addData(node.data, data, node.context.page) : data;
      const pData = derivedEntities.compute(udata, node.diffs);
      derivedEntities.entities.forEach((name) => {
        changes[name] = [node.data ? node.data[name] : undefined, pData ? pData[name] : undefined];
      });
      node.underivedData = udata;
      node.data = pData;
    } else if (node.paging) {
      node.data = data ? node.paging.addData(node.data, data, node.context.page) : data;
    } else {
      node.data = data;
    }
    changes[node.name] = [prevData, node.data];
    node.loadState = LOADED;
    if (!ctx.level) {
      logEOL();
    }
    logState(`${indent(ctx.level)}Entity ${entityName} loaded and computed`, this);

    // Update entities which have computed properties using this entity
    if (node.requiredBy.length) {
      const childCtx = Object.assign({}, ctx, {
        notify: false, // Notify subscribers of changes only once all entities depending on this data have been updated
        level: ctx.level + 1,
        passDependencyCheck: true, // Optimization to not call areDependenciesLoaded twice for the same entity
      });
      node.requiredBy.forEach((n) => {
        if (((n.loadState === LOADED) || n.pendingData)
              && n.areDependenciesLoaded()) {
          // Update only if all entities n depends on for its computed properties have been loaded
          const wasLocked = !!n.pendingData;
          if (!n.needContextualParams() || wasLocked) {
            // For data loaded on demand from a table, only update the data if it is pending
            // Will recompute computed properties of n
            this.setData(n.name, n.pendingData || n.underivedData, childCtx);
            if (wasLocked) {
              log(`${indent(ctx.level)}    => Unlock `, n.name, ': loaded and computed');
            }
          }
        }
      });
    }

    // ct.notify controls that subscribers are notified once a set of entities have been
    // updated and not after each changes
    if (ctx.notify && (!node.paging || (prevData !== node.data))) {
      const meNames = Object.keys(ctx.changes); // Names of modified entities
      // Notify subscriptions of loaded entities
      this.subscriptions.forEach((subscription) => {
        const { entitySubs, callback } = subscription;
        if (subscription.isLoaded()) {
          // Determine whether the subscription is impacted by the data changes
          // It is if at least one entity sub returns it should update
          const notify = entitySubs.find(([n, ...subs]) => {
            if (meNames.indexOf(n.name) >= 0) {
              // At least one modified entity is in the list of the subscription entities
              return subscription.needUpdate || subs.find((sub) => {
                return sub.shouldUpdate(...changes[sub.name]);
              });
            }
            if ((n.name === entityName) && subscription.needUpdate) {
              return true;
            }
            return false;
          });
          if (notify) {
            subscription.needUpdate = false;
            // At least one entity sub determines we should update the component
            // the subscription belongs to
            const entitiesData = { loading: false };
            entitySubs.forEach(([n, ...subs]) => {
              subs.forEach((sub) => {
                entitiesData[sub.name] = sub.derivedName ? n.data[sub.derivedName] : n.data;
              });
            });
            callback(entitiesData);
          }
        }
      });
    }
    return true;
  }

  isLoaded(entities) {
    return !(Array.isArray(entities) ? entities : [entities]).find((entityName) => {
      const node = this.entityNodes[this.parentEntities[entityName] || entityName];
      return !node.needContextualParams() && (node.loadState !== LOADED);
    });
  }

  sameQueries(q1, q2, sub) {
    if (!q1) {
      return !q2;
    }
    if (!q2) return false;
    if (sub.sameQueries) {
      return sub.sameQueries(q1, q2);
    }
    return q1 === q2;
  }

  updateState(state = {}, subscription, props) {
    let loading = false;
    let changed = false;
    const { entitySubs } = subscription;
    const pendingChanges = state.pendingChanges || {};
    const modified = {};
    const loadingEntities = [];
    let hasInvalidData = false;
    entitySubs.filter(([node]) => !node.needContextualParams()).forEach(([node, ...subs]) => {
      const query = subs.length && subs[0].getQuery && subs[0].getQuery(props);
      if (!this.sameQueries(query, node.context.query, subs[0])) {
        node.context.query = query;
        this.update(node.name);
        hasInvalidData = true;
        loading = true;
        loadingEntities.push(node.name);
      } else if (node.loadState !== LOADED) {
        // When multiple entities are loaded, mechanism to get
        // component using this state updated once
        if (node.loadState === NOT_LOADED) {
          this.update(node.name);
          hasInvalidData = true;
        } else if (!hasInvalidData) {
          // Look for invalid data
          hasInvalidData = !!subs.find(sub => (state[sub.derivedName || sub.name] === undefined));
        }
        loadingEntities.push(node.name);
        loading = true;
      } else {
        subs.forEach((sub) => {
          const { derivedName, name } = sub;
          const data = derivedName ? node.data[derivedName] : node.data;
          if (state[derivedName || name] === undefined) {
            hasInvalidData = true;
          }
          if (data !== (pendingChanges[name] || state[name])) {
            changed = true;
            modified[name] = data;
          }
        });
      }
    });
    if (loading) {
      if (state.loading) {
        if (changed) {
          if (!state.pendingChanges) {
            return { pendingChanges: modified };
          }
          // Still loading, stacking changes in pendingChanges
          Object.assign(state.pendingChanges, modified);
          return null;
        }
      }
      if (subscription.shouldNotifyLoading(state, { pendingChanges: modified, loadingEntities, hasInvalidData })) {
        // Ensure this suscription is notified at the end of the load
        // even if data don't change after a diff
        subscription.needUpdate = true;
        return { loading: true, pendingChanges: modified };
      }
      return null;
    }
    if (state.loading) {
      return Object.assign({ loading: false, pendingChanges: undefined }, pendingChanges, modified);
    }
    return changed && modified || null;
  }

  //
  // subscriptions
  //
  subscribe(entities, callback, { shouldNotifyLoading, ...options } = {}, props) {
    // Sort entity subscription per entity node
    // Each element of entitySubs is an array with the entity node as the first element
    // and entity subs as following elements
    const entitySubs = [];
    const entitySubsByRoot = {};
    const parentEntities = {};
    const entityTitles = {};
    entities.forEach((e) => {
      let name;
      let entitySub = { shouldUpdate: () => true };
      if (typeof e === 'string') {
        name = e;
        entitySub.name = name;
      } else {
        name = e.name;
        entitySub = Object.assign(entitySub, e);
      }
      const parent = this.parentEntities[name];
      if (parent) {
        entitySub.derivedName = name;
        parentEntities[name] = parent;
      }
      const root = parent || name;
      let subs = entitySubsByRoot[root];
      if (!subs) {
        const node = this.entityNodes[root];
        entityTitles[parent || name] = node.entity && node.entity.title || (parent || name);
        subs = [node, entitySub];
        entitySubs.push(subs);
        entitySubsByRoot[root] = subs;
      } else if (entitySub.getQuery) {
        // Store sub with query at first position
        subs.unshift(entitySub);
        [subs[0], subs[1]] = [subs[1], subs[0]];
      } else {
        subs.push(entitySub);
      }
    });
    // provided entities are either string for the entity name or objects with the properties:
    //  - name: entity name
    //  - shouldUpdate: function to determine if data changes should trigger an update to the component subsciber.
    //                  If not specified, subscriber is always updated
    const subscription = {
      ...options,
      entitySubs,
      isLoaded() { return !entitySubs.find(([node]) => (!node.needContextualParams() && (node.loadState !== LOADED))); },
      getRootEntities() { return entitySubs.map(([node]) => node.name); },
      parentEntities,
      getParentEntity(name) { return parentEntities[name]; },
      getRootEntityTitle(name) {
        return entityTitles[name] || name;
      },
      // By default, show a loading indicator if the panel for this subscription is not loaded yet or has displayed data invalid regarding to current filter
      shouldNotifyLoading: shouldNotifyLoading || ((state, { hasInvalidData }) => hasInvalidData),
      callback,
    };
    this.subscriptions.push(subscription);
    // Update entities associated with this subscription unless
    // unless all subscriptions for the entity explicitely specifies not to
    // with the shouldUpdate method
    if (!subscription.noInitUpdate) {
      entitySubs.forEach(([node, ...subs]) => {
        if (node.paging) {
          node.data = node.paging.clear();
        }
        if (!node.needContextualParams()) {
          const query = subs.length && subs[0].getQuery && subs[0].getQuery(props);
          const shouldUpdate = node.loadState === NOT_LOADED
            || !this.sameQueries(query, node.context.query, subs[0])
            || (!node.entity.static && subs.find((sub) => {
              const data = sub.derivedName ? (node.data && node.data[sub.derivedName]) : node.data;
              return sub.shouldUpdate(data);
            }));
          if (shouldUpdate) {
            this.stopPolling(node.name);
            node.context.query = query;
            if (node.diffs) {
              // If computing diffs on this entity, possible to get the new subrciption
              // not notified after the following load.
              // We tag it to enforce the notification
              subscription.needUpdate = true;
            }
            this.update(node.name);
          }
        }
      });
    }
    return subscription;
  }

  unsubscribe(subscription) {
    const index = this.subscriptions.indexOf(subscription);
    if (index >= 0) {
      this.subscriptions.splice(index, 1);
      // Look for clearing data of entities subscribed by this subscription if:
      // 1. this is the last subscription to the entity
      // 2. the unsubscribed entity is tagged with the clearOnUnsubscribe property
      subscription.entitySubs.filter(([n]) => {
        const entityName = n.name;
        // Before evaluating if the entity is unsubscribed, check if it
        // should clear if unsubscribed
        if (!n.entity.clearOnUnsubscribe) {
          return false;
        }
        // Returns truthy value if entity is unsubscribed
        // If cannot find a subscription to this entity, return true
        return !this.subscriptions
          .find(sub => (sub.getRootEntities().indexOf(entityName) >= 0));
      }).forEach(([n]) => {
        // The entity is unsubscribed and declared as clearable
        n.loadState = NOT_LOADED;
        n.data = n.defaultValue;
        this.stopPolling(n.name);
      });
      return true;
    }
    return false;
  }

  //
  // Lifecylce methods
  //
  userLoggedOut() {
    this.stopAllPollings();
  }

  userLoggedIn() {
    const subscribedEntities = this.subscriptions.reduce((r, { entitySubs, noInitUpdate }) => {
      if (!noInitUpdate) {
        entitySubs.forEach(([n]) => {
          if (!r[n.name]) {
            r[n.name] = n;
          }
        });
      }
      return r;
    }, {});
    Object.keys(this.entityNodes).forEach((name) => {
      // Clear all data and update data for entities with active subscriptions
      if (subscribedEntities[name]) {
        // This entity has at least one subscribtion
        this.update(name);
      } else {
        const n = this.entityNodes[name];
        n.loadState = NOT_LOADED;
        n.data = n.defaultValue;
      }
    });
  }
}

export default Store;
